import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Btn, InputField, Alert } from '@/presentation/component/ui';
import { useAuth } from '@/presentation/hook/useAuth';
import { resolveErrorKey } from '@/infrastructure/util/error';

const SIGNUP_ERROR_KEYS = { 409: 'auth.emailAlreadyUsed' };

export const SignupPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const { signup, isLoading, isAuthenticated, redirectPath, setRedirectPath, clearRedirectPath } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get('from');
    if (from) {
      setRedirectPath(from);
    }
  }, [location.search, setRedirectPath]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const searchParams = new URLSearchParams(location.search);
      const fromParam = searchParams.get('from');
      const destinationPath = redirectPath || fromParam || '/formation';

      const timeout = setTimeout(() => {
        clearRedirectPath();
        navigate(destinationPath, { replace: true });
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, isLoading, redirectPath, clearRedirectPath, navigate, location.search]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    try {
      await signup(formData.name, formData.email, formData.password);
    } catch (e) {
      setError(t(resolveErrorKey(e, 'auth.errorOccurred', SIGNUP_ERROR_KEYS)));
    }
  };

  const loginLink = redirectPath ? `/login?from=${encodeURIComponent(redirectPath)}` : '/login';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[var(--color-ink)]">{t('auth.signupTitle')}</h2>
          <p className="mt-2 text-[var(--color-ink-soft)]">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to={loginLink}
              className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"
            >
              {t('auth.signupSubtitle')}
            </Link>
          </p>
        </div>

        <form
          className="mt-8 space-y-6 bg-[var(--color-surface)] p-8 rounded-lg shadow-sm border border-[var(--color-line)]"
          onSubmit={handleSubmit}
        >
          {error && (
            <Alert kind="bad" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <InputField
            label={t('contact.fullName')}
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder={t('contact.fullNamePlaceholder')}
          />

          <InputField
            label={t('contact.email')}
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder={t('contact.emailPlaceholder')}
          />

          <InputField
            label={t('auth.password')}
            id="password"
            name="password"
            type="password"
            required
            value={formData.password}
            onChange={handleChange}
            placeholder={t('auth.passwordPlaceholder')}
          />

          <InputField
            label={t('auth.confirmPassword')}
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder={t('auth.confirmPasswordPlaceholder')}
          />

          <Btn
            kind="primary"
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            className="w-full"
          >
            {t('buttons.createAccount')}
          </Btn>

          <p className="text-xs text-[var(--color-ink-faint)] text-center">{t('auth.termsText')}</p>
        </form>
      </div>
    </div>
  );
};
